import React from 'react'
import { useTranslation } from 'react-i18next'
import { Bell } from 'lucide-react'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'
import './ReminderMark.scss'

export interface ReminderMarkProps {
  /** Die EIGENEN Erinnerungen der Aufgabe, beschriftet (`useOwnReminderLabels`); leer: nichts. */
  labels: readonly string[]
  /** Öffnet die Aufgaben-Details mit offenem Chip „Erinnerung“. Ohne: nur Anzeige, kein Knopf. */
  onOpen?: () => void
  /** Für das Maß der Titelzeile (`useCollapsedRowFit`). */
  'data-row-fit'?: string
  className?: string
}

/**
 * **Die Glocke an einer Zeile** (Plan task-reminders) — nur bei **eigenen** Erinnerungen: Mit der
 * Standard-Erinnerung trüge sonst jede Aufgabe mit Uhrzeit eine, und die Glocke sagte nichts mehr.
 * Der Tooltip nennt sie („30 Min. vorher · Täglich 15:00“). Wie die Kommentar-Marke ein Knopf nur,
 * wo es Aufgaben-Details gibt.
 *
 * @example
 * <ReminderMark labels={useOwnReminderLabels(task.id)} onOpen={() => opener.openReminders(task.id)} />
 */
export const ReminderMark: React.FC<ReminderMarkProps> = ({ labels, onOpen, 'data-row-fit': rowFit, className }) => {
  const { t } = useTranslation()
  if (labels.length === 0) return null
  const tooltip = t('reminders.mark.tooltip', { list: labels.join(' · ') })
  const classes = `reminder-mark${onOpen ? ' reminder-mark--button' : ''}${className ? ` ${className}` : ''}`

  if (!onOpen) {
    return (
      <WithTooltip content={tooltip}>
        <span className={classes} role="img" aria-label={tooltip} data-row-fit={rowFit}>
          <Bell size={14} aria-hidden="true" />
        </span>
      </WithTooltip>
    )
  }
  return (
    <WithTooltip content={tooltip}>
      <button
        type="button"
        className={classes}
        aria-label={t('reminders.mark.open', { list: labels.join(', ') })}
        data-row-fit={rowFit}
        // Der Druck gehört der Marke, nicht der Auswahl, dem Aufklappen oder dem Ziehen der Zeile.
        onPointerDown={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
        onDoubleClick={e => e.stopPropagation()}
        onClick={e => {
          e.stopPropagation()
          onOpen()
        }}
      >
        <Bell size={14} aria-hidden="true" />
      </button>
    </WithTooltip>
  )
}
